import { FC, useEffect, useRef } from 'react';

const TRUSTPILOT_URL =
  'https://uk.trustpilot.com/review/fantasyfootballhub.co.uk';

// Micro Star (stars only) at a compact height — the defaults when Sanity
// leaves the template/size unset.
const DEFAULT_TEMPLATE_ID = '5419b6ffb0d04a076446a9af';
const DEFAULT_HEIGHT = '40px';
const DEFAULT_WIDTH = '100%';

// Review-carrying templates (e.g. the carousel) show individual reviews;
// restrict them to 4-5 star English reviews so a stray 1-star or
// foreign-language review never fronts the paywall. Ignored by
// aggregate-only templates like Micro Star.
const REVIEW_STARS_FILTER = '4,5';
const REVIEW_LANGUAGES_FILTER = 'en';

type TrustpilotRatingProps = {
  /** TrustBox template ID. Defaults to Micro Star. */
  templateId?: string;
  /** CSS height for the widget iframe, e.g. "25px". */
  height?: string;
  /** CSS width for the widget iframe, e.g. "100%". */
  width?: string;
};

/**
 * Official Trustpilot TrustBox widget. The bootstrap script is loaded once
 * globally in _app.tsx, so here we just render the widget element and ask
 * Trustpilot to hydrate it — retrying briefly in case the script hasn't
 * finished loading by the time this mounts. Template and size are
 * author-controllable via Sanity since each template has its own natural
 * height (the iframe can't size to its own content — see TrustBox docs).
 */
const TrustpilotRating: FC<TrustpilotRatingProps> = ({
  templateId,
  height,
  width
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // Resolve with `||` (not default params): Sanity returns null/'' for unset
  // fields, and JS defaults only apply for `undefined`, so a null/empty value
  // would otherwise be taken literally and break the widget.
  const resolvedTemplateId = templateId || DEFAULT_TEMPLATE_ID;
  const resolvedHeight = height || DEFAULT_HEIGHT;
  const resolvedWidth = width || DEFAULT_WIDTH;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const tryLoad = () => {
      if (window.Trustpilot && ref.current) {
        // Never let a third-party widget error take down the funnel.
        try {
          window.Trustpilot.loadFromElement(ref.current, true);
        } catch (error) {
          console.error('Trustpilot widget failed to load', error);
        }
        return;
      }
      if (attempts < 20) {
        attempts += 1;
        timer = setTimeout(tryLoad, 250);
      }
    };

    tryLoad();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resolvedTemplateId, resolvedHeight, resolvedWidth]);

  return (
    <div
      ref={ref}
      className="trustpilot-widget"
      data-locale="en-GB"
      data-template-id={resolvedTemplateId}
      data-businessunit-id="61e6f3bfa6bec4c5bbb7a049"
      data-style-height={resolvedHeight}
      data-style-width={resolvedWidth}
      data-stars={REVIEW_STARS_FILTER}
      data-review-languages={REVIEW_LANGUAGES_FILTER}
      data-token="d806683c-e904-416e-b270-d12f3b891601"
    >
      <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
};

export default TrustpilotRating;
